import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  NumberInput,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import {
  PiCaretDownBold,
  PiCaretUpBold,
  PiPackageDuotone,
  PiPlusBold,
} from 'react-icons/pi';
import { useLocation } from 'wouter';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import RichTooltip from '@/components/common/RichTooltip';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import JurisdictionsSelect from '@/components/JurisdictionsSelect/JurisdictionsSelect';
import { useServicesFilters } from '@/hooks/useServicesFilters';
import { JURISDICTION_LIST } from '@/lib/constants';
import { formatPrice } from '@/lib/helpers';
import { TService, useServicesList } from '@/models/services';
import { ServiceListParams, ServiceSortField } from '@/types';

const SERVICE_TYPES = [
  'Corporate Services',
  'License obtainment assistance',
  'Document preparation',
  'Legal Services',
  'Account opening assistance',
  'Reselling Agent Services',
  'In-House',
  'Nominee Director and Shareholder',
];

export default function ServicesPage() {
  const [_, nav] = useLocation();
  const { filters, setState, clearFilters } = useServicesFilters();

  const appliedKey = JSON.stringify({
    term: filters.term,
    jurisdiction: filters.jurisdiction,
    strictJurisdiction: filters.strictJurisdiction,
    currency: filters.currency,
    type: filters.type,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    serviceFilter: filters.serviceFilter,
  });

  const [draftFilters, setDraftFilters] = useState<Partial<ServiceListParams>>(
    () => JSON.parse(appliedKey),
  );

  useEffect(() => {
    setDraftFilters(JSON.parse(appliedKey));
  }, [appliedKey]);

  const [filtersOpened, setFiltersOpened] = useState(true);

  const { data, isLoading, error } = useServicesList(filters);

  // Apply draft filters to URL and trigger API call
  const handleApplyFilters = () => {
    setState({
      page: 1,
      term: draftFilters.term ?? '',
      jurisdiction: draftFilters.jurisdiction ?? '',
      strictJurisdiction: draftFilters.strictJurisdiction ?? false,
      currency: draftFilters.currency ?? '',
      type: draftFilters.type ?? '',
      minPrice: draftFilters.minPrice ?? null,
      maxPrice: draftFilters.maxPrice ?? null,
      serviceFilter: draftFilters.serviceFilter ?? '',
    });
  };

  // Clear all filters (both draft and URL)
  const handleClearFilters = () => {
    setDraftFilters({});
    clearFilters();
  };

  // Handle sorting
  const handleSortChange = (field: string) => {
    // Map 'children' accessor to 'childrenCount' sort field
    const sortField = field === 'children' ? 'childrenCount' : field;

    setState(
      {
        sortBy: sortField as ServiceSortField,
        sortOrder:
          filters.sortBy === sortField && filters.sortOrder === 'desc'
            ? 'asc'
            : 'desc',
      },
      { resetPage: false },
    );
  };

  // Check if any filters are applied (beyond defaults)
  const hasActiveFilters =
    filters.term ||
    filters.jurisdiction ||
    filters.strictJurisdiction ||
    filters.type ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.currency ||
    filters.serviceFilter;

  // Check if draft filters differ from applied filters (memoized for performance)
  const hasUnappliedChanges = useMemo(
    () =>
      draftFilters.term !== filters.term ||
      draftFilters.jurisdiction !== filters.jurisdiction ||
      (draftFilters.strictJurisdiction ?? false) !==
        (filters.strictJurisdiction ?? false) ||
      draftFilters.type !== filters.type ||
      draftFilters.currency !== filters.currency ||
      draftFilters.minPrice !== filters.minPrice ||
      draftFilters.maxPrice !== filters.maxPrice ||
      draftFilters.serviceFilter !== filters.serviceFilter,
    [draftFilters, filters],
  );

  // Define table columns (memoized to prevent recreation on every render)
  const columns = useMemo<GenericTableColumn<TService>[]>(
    () => [
      {
        accessor: 'name',
        title: 'Service Name',
        sortable: true,
        render: (service) => (
          <Stack gap={0}>
            <Group gap={4}>
              <Text size="xs" fw={500}>
                {service.name}
              </Text>
              {service.standalone && (
                <Badge size="xs" variant="light" color="gray">
                  Standalone
                </Badge>
              )}
              {service.isAdditionalService && (
                <Badge size="xs" variant="light" color="blue">
                  Additional
                </Badge>
              )}
            </Group>
          </Stack>
        ),
      },
      {
        accessor: 'type',
        title: 'Type',
        sortable: true,
        width: 200,
        render: (service) => <Text size="xs">{service.type}</Text>,
      },
      {
        accessor: 'children',
        title: 'Package',
        sortable: true,
        width: 100,
        render: (service) =>
          service.children.length > 0 ? (
            <RichTooltip
              content={
                <Stack gap={3}>
                  {service.children.map((child) => (
                    <Text size="xs" key={child.id} c="dimmed">
                      - {child.name}
                    </Text>
                  ))}
                </Stack>
              }
            >
              <Group gap={4}>
                <PiPackageDuotone size={16} />
                <Badge size="xs" variant="light" color="gray">
                  {service.children.length}
                </Badge>
              </Group>
            </RichTooltip>
          ) : (
            <>-</>
          ),
      },
      {
        accessor: 'jurisdictions',
        title: 'Jurisdictions',
        sortable: false,
        width: 150,
        render: (service) => (
          <Tooltip
            label={
              service.jurisdictions?.length > 0
                ? service.jurisdictions
                    .map(
                      (j) =>
                        JURISDICTION_LIST.find((jur) => jur.code === j)?.title,
                    )
                    .join(', ')
                : 'All jurisdictions'
            }
          >
            <Text size="xs" lineClamp={1}>
              {service.jurisdictions?.length > 0
                ? service.jurisdictions.length === 1
                  ? JURISDICTION_LIST.find(
                      (jur) => jur.code === service.jurisdictions[0],
                    )?.title
                  : `${service.jurisdictions.length} jurisdictions`
                : 'All jurisdictions'}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessor: 'price',
        title: 'Price',
        sortable: true,
        width: 120,
        render: (service) => (
          <Text size="xs" fw={500}>
            {formatPrice(service.price, service.currency)}
          </Text>
        ),
      },
      {
        accessor: 'updatedAt',
        title: 'Updated',
        sortable: true,
        width: 150,
        render: (service) => (
          <DateSinceDisplay date={service.updatedAt} size="xs" c="dimmed" />
        ),
      },
    ],
    [],
  );

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between">
        <Button
          variant="subtle"
          size="xs"
          onClick={() => setFiltersOpened(!filtersOpened)}
          rightSection={filtersOpened ? <PiCaretUpBold /> : <PiCaretDownBold />}
        >
          {filtersOpened ? 'Hide Filters' : 'Show Filters'}
        </Button>
        <Button
          leftSection={<PiPlusBold />}
          variant="light"
          onClick={() => nav('/add')}
          size="xs"
        >
          Add new service
        </Button>
      </Group>

      {/* Filters */}
      <Collapse expanded={filtersOpened}>
        <Stack gap="sm">
          <Group gap="sm" align="flex-end">
            <TextInput
              label="Search"
              placeholder="Search by service name"
              value={draftFilters.term || ''}
              onChange={(e) => {
                const term = e.currentTarget.value || undefined;
                setDraftFilters((prev) => ({ ...prev, term }));
              }}
              size="xs"
              style={{ width: 250 }}
            />

            <Box style={{ width: 180 }}>
              <JurisdictionsSelect
                value={draftFilters.jurisdiction}
                onChange={(value) =>
                  setDraftFilters((prev) => ({ ...prev, jurisdiction: value }))
                }
                strict={draftFilters.strictJurisdiction ?? false}
                onStrictChange={(v) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    strictJurisdiction: v,
                  }))
                }
              />
            </Box>

            <Select
              label="Service Type"
              placeholder="All types"
              value={draftFilters.type || null}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  type: value || undefined,
                }))
              }
              data={SERVICE_TYPES}
              clearable
              size="xs"
              style={{ width: 200 }}
            />

            <Select
              label="Currency"
              placeholder="All"
              value={draftFilters.currency || null}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  currency: value || undefined,
                }))
              }
              data={['EUR', 'USD', 'GBP']}
              clearable
              size="xs"
              style={{ width: 100 }}
            />

            <NumberInput
              label="Min Price"
              placeholder="0"
              value={draftFilters.minPrice ?? ''}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  minPrice: value ? Number(value) : undefined,
                }))
              }
              min={0}
              size="xs"
              style={{ width: 90 }}
            />

            <NumberInput
              label="Max Price"
              placeholder="∞"
              value={draftFilters.maxPrice ?? ''}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  maxPrice: value ? Number(value) : undefined,
                }))
              }
              min={0}
              size="xs"
              style={{ width: 90 }}
            />
          </Group>

          <Group gap="sm" align="center">
            <Box>
              <Text size="xs" fw={500} mb={4}>
                Package Type
              </Text>
              <SegmentedControl
                value={draftFilters.serviceFilter || 'all'}
                onChange={(value) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    serviceFilter: value === 'all' ? undefined : (value as any),
                  }))
                }
                data={[
                  { label: 'All', value: 'all' },
                  { label: 'Package', value: 'packages' },
                  { label: 'Standalone', value: 'standalone' },
                ]}
                size="xs"
              />
            </Box>

            <Button
              mt={20}
              variant="filled"
              size="xs"
              onClick={handleApplyFilters}
              disabled={!hasUnappliedChanges}
            >
              Apply Filters
            </Button>

            {hasActiveFilters && (
              <Button variant="light" size="xs" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Group>
        </Stack>
      </Collapse>

      {/* Error state */}
      {error && (
        <Text c="red" size="sm">
          Error loading services:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </Text>
      )}

      {/* Debug info */}
      {!isLoading && !error && (
        <Text size="xs" c="dimmed">
          Total records: {data?.total || 0} | Services:{' '}
          {data?.services?.length || 0}
        </Text>
      )}

      {/* Table */}
      <GenericTable<TService>
        records={data?.services || []}
        columns={columns}
        loading={isLoading}
        sortBy={
          filters.sortBy === 'childrenCount' ? 'children' : filters.sortBy
        }
        sortOrder={filters.sortOrder}
        onSort={handleSortChange}
        emptyMessage="No services found"
        idAccessor="id"
        onRowClick={(service) => {
          nav(`/${(service as TService).id}`);
        }}
      />

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Center>
          <Pagination
            total={data.totalPages}
            value={filters.page || 1}
            onChange={(page) => setState({ page })}
            size="sm"
          />
        </Center>
      )}

      {/* Summary */}
      {data && (
        <Text size="xs" c="dimmed">
          Showing {data.services.length} of {data.total} services (Page{' '}
          {data.page} of {data.totalPages})
        </Text>
      )}
    </Stack>
  );
}
